import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  PhoneCall, MapPin, CalendarDays, Loader2, ArrowLeft, CheckCircle2, XCircle, RefreshCw, ShieldCheck, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRAND_ORANGE = '#FF7816';
const BRAND_GREEN = '#27B84A';
const BRAND_GREEN_DARK = '#118A35';

const to12h = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtDate = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

function BrandHeader({ label = 'Manage your appointment' }) {
  return (
    <header className="border-b border-[#E4E8E6] bg-white">
      <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#18202D] font-bold">
          <ArrowLeft className="w-4 h-4 text-slate-500" />
          <img src="/cb-logo.png" alt="CountBucks" className="h-8 w-8" />
          <span className="text-lg tracking-tight">
            <span style={{ color: BRAND_ORANGE }}>Count</span>
            <span style={{ color: BRAND_GREEN_DARK }}>Bucks</span>
          </span>
        </Link>
        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4" style={{ color: BRAND_GREEN }} /> {label}
        </div>
      </div>
    </header>
  );
}

export default function ManageAppointment() {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [mode, setMode] = useState('view');
  const [cancelResult, setCancelResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const [date, setDate] = useState('');
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState('');

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const r = await fetch(`${API_URL}/api/public/appointments/manage/${token}`);
      if (r.status === 404) { setNotFound(true); return; }
      if (!r.ok) { setLoadError(true); return; }
      setAppt(await r.json());
    } catch { setLoadError(true); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setSlot('');
    if (!date || !appt) { setSlots(null); return; }
    setSlots(null);
    fetch(`${API_URL}/api/public/appointments/availability?date=${date}&duration=${appt.duration_minutes}`)
      .then(async (r) => setSlots(r.ok ? (await r.json()).slots : 'error'))
      .catch(() => setSlots('error'));
  }, [date, appt]);

  const isInPerson = appt?.meeting_type === 'in_person';
  const kind = isInPerson ? 'meeting' : 'call';

  const doCancel = async () => {
    const cutoff = appt.refund_cutoff_hours || 48;
    let msg;
    if (appt.paid_with_credits) {
      msg = appt.refund_eligible
        ? `Cancel this appointment? Your ${appt.duration_minutes} booking-credit minutes will be returned to your CountBucks account.`
        : `Cancel this appointment? Because the ${kind} is within ${cutoff} hours, your booking credits are forfeited.`;
    } else {
      msg = appt.refund_eligible
        ? 'Cancel this appointment? Your payment will be fully refunded to your card.'
        : `Cancel this appointment? Because the ${kind} is within ${cutoff} hours, the payment is non-refundable.`;
    }
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/appointments/manage/${token}/cancel`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) { toast.error(j.detail || 'Cancel failed'); return; }
      setCancelResult(j);
      setMode('cancelled');
    } catch { toast.error('Cancel failed'); }
    finally { setBusy(false); }
  };

  const doReschedule = async () => {
    if (!date || !slot) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/appointments/manage/${token}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, start_time: slot }),
      });
      const j = await r.json();
      if (!r.ok) { toast.error(j.detail || 'Reschedule failed'); return; }
      toast.success('Appointment rescheduled — updated confirmation email sent');
      setMode('view');
      setDate('');
      load();
    } catch { toast.error('Reschedule failed'); }
    finally { setBusy(false); }
  };

  const minDate = new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10);
  const maxDate = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-white text-[#18202D]">
      <BrandHeader />
      <div className="max-w-xl mx-auto px-4 py-14" data-testid="manage-appointment-page">
        {notFound ? (
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Appointment not found</h1>
            <p className="text-slate-600">This link may be invalid. Need help? Reply to your confirmation email.</p>
          </div>
        ) : loadError ? (
          <div className="text-center" data-testid="manage-load-error">
            <XCircle className="w-12 h-12 mx-auto mb-4" style={{ color: BRAND_ORANGE }} />
            <h1 className="text-2xl font-bold mb-2">Could not load your appointment</h1>
            <p className="text-slate-600 mb-4">Check your connection and try again.</p>
            <Button onClick={load} variant="outline" className="border-[#E4E8E6] text-[#18202D]">Retry</Button>
          </div>
        ) : !appt ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: BRAND_GREEN }} />
          </div>
        ) : mode === 'cancelled' ? (
          <div className="text-center" data-testid="manage-cancelled-state">
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: BRAND_GREEN }} />
            <h1 className="text-2xl font-bold mb-3">Appointment cancelled</h1>
            <p className="text-slate-600">
              {cancelResult?.paid_with_credits ? (
                cancelResult?.refunded
                  ? `Your ${cancelResult.credits_refunded_minutes} booking-credit minutes have been returned to your CountBucks account.`
                  : cancelResult?.refund_eligible
                    ? "Your credits will be restored — we'll email you once it's done."
                    : `Booking credits are forfeited because the cancellation was within ${cancelResult?.cutoff_hours || 24} hours of the ${kind}.`
              ) : cancelResult?.refunded
                ? `Your $${appt.amount.toFixed(2)} payment has been refunded — it typically appears on your card within 5–10 business days.`
                : cancelResult?.refund_eligible
                ? 'Your refund is being processed and will arrive within a few business days.'
                : `The payment is non-refundable because the cancellation was within ${cancelResult?.cutoff_hours || 48} hours of the ${kind}.`}
            </p>
            <Button asChild className="mt-6 text-white" style={{ backgroundColor: BRAND_GREEN }}>
              <Link to="/book">Book a new time</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                   style={{ backgroundColor: '#F2FBF4', color: BRAND_GREEN_DARK, border: `1px solid ${BRAND_GREEN}40` }}>
                {isInPerson ? <MapPin className="w-3.5 h-3.5" /> : <PhoneCall className="w-3.5 h-3.5" />}
                Internal CPA Advisor
              </div>
              <h1 className="text-3xl font-bold">Your appointment</h1>
            </div>

            <div className="rounded-2xl bg-white border border-[#E4E8E6] p-6 shadow-sm" data-testid="manage-appt-card">
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <span className="font-bold"
                        style={{ color: appt.status === 'confirmed' ? BRAND_GREEN_DARK : '#DC2626' }}>
                    {appt.status === 'confirmed' ? 'Confirmed' : appt.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Type</span>
                  <span className="font-semibold flex items-center gap-1">
                    {isInPerson
                      ? <><MapPin className="w-3.5 h-3.5" style={{ color: BRAND_ORANGE }} /> In-Person Meeting</>
                      : <><PhoneCall className="w-3.5 h-3.5" style={{ color: BRAND_GREEN_DARK }} /> Phone Call</>}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">When</span>
                  <span className="font-semibold text-right">{fmtDate(appt.date)} · {to12h(appt.start_time)} ET</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Duration</span>
                  <span className="font-semibold">{appt.duration_minutes} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{appt.paid_with_credits ? 'Booked with' : 'Paid'}</span>
                  <span className="font-bold" style={{ color: BRAND_GREEN_DARK }}>
                    {appt.paid_with_credits ? `${appt.duration_minutes} min credit` : `$${appt.amount.toFixed(2)}`}
                  </span>
                </div>
                {isInPerson && appt.location ? (
                  <div className="mt-2 rounded-lg border border-[#E4E8E6] bg-[#F7F9F8] p-3 flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BRAND_ORANGE }} />
                    <div className="text-sm">
                      <p className="font-bold text-[#18202D]">Where</p>
                      <p className="text-slate-600 mt-0.5">{appt.location}</p>
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appt.location)}`}
                         target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 mt-1 text-xs font-semibold hover:underline"
                         style={{ color: BRAND_GREEN_DARK }}
                         data-testid="manage-map-link">
                        Open in Google Maps <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-slate-500">We call</span>
                    <span className="font-semibold">{appt.phone}</span>
                  </div>
                )}
              </div>

              {appt.status === 'confirmed' && !appt.is_past && (
                <>
                  <div className="mt-4 rounded-lg bg-[#F7F9F8] border border-[#E4E8E6] p-3 text-xs text-slate-600"
                       data-testid="refund-policy-note">
                    {appt.paid_with_credits ? (
                      appt.refund_eligible
                        ? `Cancel now to reclaim your ${appt.duration_minutes} booking-credit minutes (${appt.refund_cutoff_hours || 24}+ hours before your ${kind}). Reschedules are always free.`
                        : `Your ${kind} is within ${appt.refund_cutoff_hours || 24} hours — booking credits are forfeited on cancel, but you can still reschedule for free.`
                    ) : (
                      appt.refund_eligible
                        ? `Cancel now for a full refund (${appt.refund_cutoff_hours || 48}+ hours before your ${kind}). Reschedules are always free.`
                        : `Your ${kind} is within ${appt.refund_cutoff_hours || 48} hours — cancellations are non-refundable, but you can still reschedule for free.`
                    )}
                  </div>
                  <div className="flex gap-3 mt-5">
                    <Button
                      onClick={() => setMode(mode === 'reschedule' ? 'view' : 'reschedule')}
                      className="flex-1 text-white font-semibold"
                      style={{ backgroundColor: BRAND_GREEN }}
                      data-testid="manage-reschedule-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> Reschedule
                    </Button>
                    <Button
                      onClick={doCancel}
                      disabled={busy}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="manage-cancel-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel{appt.refund_eligible ? (appt.paid_with_credits ? ' (credit back)' : ' (full refund)') : ''}
                    </Button>
                  </div>
                </>
              )}
            </div>

            {mode === 'reschedule' && (
              <div className="rounded-2xl bg-white border border-[#E4E8E6] p-6 mt-5 shadow-sm" data-testid="reschedule-panel">
                <h2 className="text-sm font-bold text-[#18202D] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" style={{ color: BRAND_GREEN_DARK }} /> Pick a new time (ET)
                </h2>
                <Input
                  type="date" value={date} min={minDate} max={maxDate}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-white border-[#E4E8E6] text-[#18202D] max-w-xs"
                  data-testid="reschedule-date-input"
                />
                {date && (
                  slots === null ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm mt-4">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                  ) : slots === 'error' ? (
                    <p className="text-sm text-red-600 mt-4">Could not load times — try again.</p>
                  ) : slots.length === 0 ? (
                    <p className="text-sm font-semibold mt-4" style={{ color: BRAND_ORANGE }}>
                      No open slots that day — try another weekday.
                    </p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2 mt-4">
                      {slots.map((s) => {
                        const sel = slot === s;
                        return (
                          <button key={s} onClick={() => setSlot(s)} data-testid={`reslot-${s}`}
                            className={`rounded-lg px-2 py-2 text-sm font-semibold border-2 transition-all ${
                              sel ? 'text-white' : 'bg-white border-[#E4E8E6] text-[#18202D] hover:border-[#27B84A]'
                            }`}
                            style={sel ? { backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN } : {}}>
                            {to12h(s)}
                          </button>
                        );
                      })}
                    </div>
                  )
                )}
                <Button
                  onClick={doReschedule}
                  disabled={!date || !slot || busy}
                  className="w-full mt-5 text-white font-bold"
                  style={{ backgroundColor: BRAND_GREEN }}
                  data-testid="reschedule-confirm-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm new time (free)'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
